import React, { useState } from 'react';
import { TagInput } from '../components/TagInput';
import { ProfilePictureUpload } from '../components/ProfilePictureUpload';
import { ArrowRight, Check, ChefHat, User } from 'lucide-react';
import { UserProfile } from '../App';
interface OnboardingPageProps {
  onComplete: (profile: UserProfile) => void;
  initialData?: UserProfile | null;
}
type UserRole = 'guest' | 'cook' | null;
type CookingLevel = 'beginner' | 'intermediate' | 'master' | null;
const APPLIANCES = ['Oven', 'Stove Top', 'Microwave', 'Air Fryer', 'Instant Pot', 'Pressure Cooker', 'Slow Cooker', 'Grill', 'Toaster Oven', 'Blender', 'Food Processor', 'Sous Vide'];
export function OnboardingPage({
  onComplete,
  initialData
}: OnboardingPageProps) {
  const [step, setStep] = useState(1);
  // Form State - initialize with existing data if editing
  const [name, setName] = useState(initialData?.name || '');
  const [avatar, setAvatar] = useState<string | null>(initialData?.avatar || null);
  const [dietaryRequirements, setDietaryRequirements] = useState<string[]>([]);
  const [dietaryRequests, setDietaryRequests] = useState<string[]>([]);
  const [avoidList, setAvoidList] = useState<string[]>([]);
  const [role, setRole] = useState<UserRole>(null);
  const [cookingLevel, setCookingLevel] = useState<CookingLevel>(null);
  const [appliances, setAppliances] = useState<string[]>([]);
  const [cookingPreferences, setCookingPreferences] = useState<string[]>([]);
  const handleNext = () => setStep(s => s + 1);
  const handleComplete = () => {
    onComplete({
      name,
      avatar
    });
  };
  const toggleAppliance = (appliance: string) => {
    setAppliances(prev => prev.includes(appliance) ? prev.filter(a => a !== appliance) : [...prev, appliance]);
  };
  const renderStep1 = () => <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-993">
      <div className="space-y-2" data-id="element-994">
        <h2 className="text-3xl font-bold text-gray-900" data-id="element-995">
          Welcome! Let's get to know you
        </h2>
        <p className="text-gray-500" data-id="element-996">
          First things first, who are we cooking for?
        </p>
      </div>

      <div className="space-y-6" data-id="element-997">
        <div className="space-y-2" data-id="element-998">
          <label className="block font-mono text-sm uppercase tracking-wider text-gray-500" data-id="element-999">
            Display Name
          </label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Chef Alex" className="w-full border-2 border-gray-300 bg-transparent p-4 font-mono text-lg focus:border-gray-800 focus:outline-none" autoFocus data-id="element-1000" />
        </div>

        <ProfilePictureUpload selectedAvatar={avatar} onSelect={setAvatar} data-id="element-1001" />
      </div>

      <div className="pt-8 flex justify-end" data-id="element-1002">
        <button onClick={handleNext} disabled={!name.trim()} className="flex items-center px-8 py-4 bg-gray-900 text-white font-mono uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1003">
          Continue <ArrowRight className="ml-2 w-4 h-4" data-id="element-1004" />
        </button>
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1005">
      <div className="space-y-2" data-id="element-1006">
        <h2 className="text-3xl font-bold text-gray-900" data-id="element-1007">
          Tell us about your eating preferences
        </h2>
        <p className="text-gray-500" data-id="element-1008">
          We'll customize every recipe to match your taste profile.
        </p>
      </div>

      <div className="space-y-8" data-id="element-1009">
        <TagInput label="Dietary Requirements & Allergies" description="We will ensure that meals generated for you will not conflict with these requirements." placeholder="e.g. Peanuts, Shellfish, Gluten..." tags={dietaryRequirements} onTagsChange={setDietaryRequirements} data-id="element-1010" />

        <TagInput label="Dietary Requests" description="Fan of larger portions? Smother everything in cheese? Make it spicy? What should we consider?" placeholder="e.g. Spicy, High Protein, Low Carb..." tags={dietaryRequests} onTagsChange={setDietaryRequests} data-id="element-1011" />

        <TagInput label="What should we avoid?" description="Any cuisines or ingredients you prefer not to have? We'll try to respect these wishes." placeholder="e.g. Cilantro, Mushrooms, Olives..." tags={avoidList} onTagsChange={setAvoidList} data-id="element-1012" />
      </div>

      <div className="pt-8 flex justify-end" data-id="element-1013">
        <button onClick={handleNext} className="flex items-center px-8 py-4 bg-gray-900 text-white font-mono uppercase tracking-widest hover:bg-gray-800 transition-colors" data-id="element-1014">
          Continue <ArrowRight className="ml-2 w-4 h-4" data-id="element-1015" />
        </button>
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1016">
      <div className="space-y-2" data-id="element-1017">
        <h2 className="text-3xl font-bold text-gray-900" data-id="element-1018">One more thing...</h2>
        <p className="text-gray-500" data-id="element-1019">
          Will you be preparing meals or just setting preferences as a guest?
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1020">
        <button onClick={() => setRole('guest')} className={`p-6 border-2 text-left transition-all ${role === 'guest' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-1021">
          <User className="w-8 h-8 mb-4 text-gray-700" data-id="element-1022" />
          <h3 className="font-bold text-lg mb-1" data-id="element-1023">Just a Guest</h3>
          <p className="text-sm text-gray-500" data-id="element-1024">
            I'm here to eat, not to cook. Just save my preferences.
          </p>
        </button>

        <button onClick={() => setRole('cook')} className={`p-6 border-2 text-left transition-all ${role === 'cook' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-1025">
          <ChefHat className="w-8 h-8 mb-4 text-gray-700" data-id="element-1026" />
          <h3 className="font-bold text-lg mb-1" data-id="element-1027">I'll be Cooking</h3>
          <p className="text-sm text-gray-500" data-id="element-1028">
            I want to generate recipes and manage my kitchen setup.
          </p>
        </button>
      </div>

      {role === 'cook' && <div className="space-y-8 pt-8 border-t-2 border-dashed border-gray-200 animate-in fade-in slide-in-from-top-2" data-id="element-1029">
          <div className="space-y-4" data-id="element-1030">
            <label className="block font-mono text-sm uppercase tracking-wider text-gray-500" data-id="element-1031">
              Cooking Level
            </label>
            <div className="grid grid-cols-3 gap-4" data-id="element-1032">
              {[{
            id: 'beginner',
            label: 'Beginner',
            icon: '🍳'
          }, {
            id: 'intermediate',
            label: 'Intermediate',
            icon: '👨‍🍳'
          }, {
            id: 'master',
            label: 'Master Chef',
            icon: '👑'
          }].map(level => <button key={level.id} onClick={() => setCookingLevel(level.id as CookingLevel)} className={`p-4 border-2 flex flex-col items-center justify-center gap-2 transition-all ${cookingLevel === level.id ? 'border-gray-900 bg-gray-100' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-1033">
                  <span className="text-2xl" data-id="element-1034">{level.icon}</span>
                  <span className="font-mono text-sm" data-id="element-1035">{level.label}</span>
                </button>)}
            </div>
          </div>

          <div className="space-y-4" data-id="element-1036">
            <label className="block font-mono text-sm uppercase tracking-wider text-gray-500" data-id="element-1037">
              Available Appliances
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-id="element-1038">
              {APPLIANCES.map(appliance => <button key={appliance} onClick={() => toggleAppliance(appliance)} className={`px-3 py-2 text-sm border font-mono text-left transition-colors ${appliances.includes(appliance) ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 hover:border-gray-500 text-gray-600'}`} data-id="element-1039">
                  {appliances.includes(appliance) ? '[x]' : '[ ]'} {appliance}
                </button>)}
            </div>
          </div>

          <TagInput label="Cooking Style Preferences" description="Any specific techniques or styles you prefer?" placeholder="e.g. One-pot meals, 30-minute recipes, Meal prep..." tags={cookingPreferences} onTagsChange={setCookingPreferences} data-id="element-1040" />
        </div>}

      <div className="pt-8 flex justify-end" data-id="element-1041">
        <button onClick={handleComplete} disabled={!role || role === 'cook' && !cookingLevel} className="flex items-center px-8 py-4 bg-gray-900 text-white font-mono uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1042">
          Complete Setup <Check className="ml-2 w-4 h-4" data-id="element-1043" />
        </button>
      </div>
    </div>;
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] p-4 md:p-8 font-sans flex items-center justify-center" data-id="element-1044">
      <div className="w-full max-w-3xl bg-white border-2 border-dashed border-gray-300 p-8 md:p-12 shadow-sm" data-id="element-1045">
        <div className="flex items-center justify-between mb-12 border-b border-gray-100 pb-6" data-id="element-1046">
          <div className="flex space-x-2" data-id="element-1047">
            {[1, 2, 3].map(s => <div key={s} className={`h-2 w-12 rounded-full transition-colors ${s <= step ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-1048" />)}
          </div>
          <span className="font-mono text-xs text-gray-400 uppercase tracking-widest" data-id="element-1049">
            Step 0{step} / 03
          </span>
        </div>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </div>;
}